/**
 * PermissionGuard Component
 * Wraps content and checks if user has required permission level
 */

import React from 'react';
import { User } from '../types';
import { PageKey, PermissionLevel } from '../types/permissions';
import { usePermissions } from '../hooks/usePermissions';
import AccessDenied from './AccessDenied';
import { Loader2 } from 'lucide-react';

interface PermissionGuardProps {
  user?: User;
  orgId?: string;
  page: PageKey;
  requiredLevel?: PermissionLevel;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * Wraps content with permission check
 * Shows AccessDenied if user doesn't have required permission
 */
const PermissionGuard: React.FC<PermissionGuardProps> = ({
  user,
  orgId,
  page,
  requiredLevel = 'read',
  children,
  fallback,
}) => {
  const { hasAccess, loading } = usePermissions(user, orgId);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-accent" />
      </div>
    );
  }

  if (!hasAccess(page, requiredLevel)) {
    return fallback || <AccessDenied />;
  }

  return <>{children}</>;
};

export default PermissionGuard;

